import {
  CURRENT_CHECKLIST_VERSION,
  getChecklistCatalogue,
  isChecklistStepKey,
  type AdminReportVoiceNoteDto,
} from '@carcheck/shared';
import type { TFunction } from 'i18next';

/**
 * Voice-note helpers for the report media tab (AUT-429).
 *
 * Order: step notes follow the inspection ROUTE (catalogue `order`, not the
 * key array index and not wire order), step keys this build doesn't know go
 * after the known ones in wire order, and the summary («Итог») is last. The
 * admin DTO carries no checklist version, so the current catalogue is the
 * reference — a retired key simply sorts into the «unknown» bucket.
 */

/** A summary note: no step, or explicitly the summary purpose. */
export const isSummaryVoiceNote = (note: AdminReportVoiceNoteDto): boolean =>
  note.stepKey === null || note.purpose === 'summary_voice';

const stepRanks = (): ReadonlyMap<string, number> =>
  new Map(
    (getChecklistCatalogue(CURRENT_CHECKLIST_VERSION) ?? []).map((step) => [step.key, step.order]),
  );

const UNKNOWN_STEP_BUCKET = 1;
const SUMMARY_BUCKET = 2;

export const sortVoiceNotes = (
  notes: readonly AdminReportVoiceNoteDto[],
): AdminReportVoiceNoteDto[] => {
  const ranks = stepRanks();
  const bucketOf = (note: AdminReportVoiceNoteDto): number => {
    if (isSummaryVoiceNote(note)) return SUMMARY_BUCKET;
    return ranks.has(note.stepKey ?? '') ? 0 : UNKNOWN_STEP_BUCKET;
  };
  const rankOf = (note: AdminReportVoiceNoteDto): number => ranks.get(note.stepKey ?? '') ?? 0;

  // `Array.prototype.sort` is stable, so equal buckets keep wire order.
  return [...notes].sort((a, b) => bucketOf(a) - bucketOf(b) || rankOf(a) - rankOf(b));
};

/**
 * Visible title of a checklist step, resolved through i18n
 * (`checklist.step.<key>.title`, registered from the shared bundle). An
 * unknown key never crashes and never shows an i18n path — it falls back to
 * the shared «Пункт осмотра» (`checklist.fallback.step`, same bundle).
 */
export const voiceNoteStepTitle = (stepKey: string, t: TFunction): string => {
  const fallback = t('checklist.fallback.step');
  return isChecklistStepKey(stepKey)
    ? t(`checklist.step.${stepKey}.title`, { defaultValue: fallback })
    : fallback;
};
